/**
 * The workspace's mobile "More" menu — a bottom sheet holding everything the
 * desktop toolbar shows as icon buttons (panels, report/share, profile, theme),
 * opened from the 4th bottom-nav tab on phones. Each row closes the sheet and
 * fires the exact same handler its desktop toolbar twin uses, so the two
 * surfaces can never drift.
 *
 * @module
 */

import type { IconName } from '@molecule/app-icons'
import { useTheme, useTranslation } from '@molecule/app-react'

import type { WorkspacePanel } from '../utilities/workspacePanels.js'
import { Icon } from './Icon.js'
import { MobileSheet } from './MobileSheet.js'

/** Props for {@link WorkspaceMoreSheet}. */
export interface WorkspaceMoreSheetProps {
  /** Whether the sheet is shown. */
  open: boolean
  /** Dismiss handler. */
  onClose: () => void
  /** Open one of the workspace panels (Storage/Environment/Activity/Logs/Pipeline). */
  onOpenPanel: (panel: WorkspacePanel) => void
  /** Toggle the terminal dock (advanced mode only; `null` hides the row). */
  onToggleTerminal: (() => void) | null
  /** Open the in-chat bug-report modal. */
  onReport: () => void
  /** Open the in-chat share modal. */
  /** Null hides the row (a read-only viewer cannot mint share links). */
  onShare: (() => void) | null
  /**
   * False for read-only viewers: the Environment / Activity / Logs rows are
   * hidden — their panels' reads are editor+ server-side and would only 403.
   */
  canEdit?: boolean
  /** Open the profile (or the signup modal for guests). */
  onProfile: () => void
  /** Warning-badge count for the Environment row (missing env vars). */
  envMissingCount: number
  /** Whether sandbox-dependent rows are enabled. */
  sandboxRunning: boolean
  /** Guests see "Sign up" instead of "Your profile" (that's where the row goes). */
  isGuest: boolean
}

interface SheetRow {
  key: string
  icon: IconName
  label: string
  onClick: () => void
  disabled?: boolean
  badge?: number
}

/**
 * Renders the mobile More menu.
 * @param props - Component props.
 * @returns The bottom sheet.
 */
export function WorkspaceMoreSheet({
  open,
  onClose,
  onOpenPanel,
  onToggleTerminal,
  onReport,
  onShare,
  canEdit,
  onProfile,
  envMissingCount,
  sandboxRunning,
  isGuest,
}: WorkspaceMoreSheetProps) {
  const { t } = useTranslation()
  const { mode, toggleTheme } = useTheme()

  const run = (fn: () => void) => () => {
    onClose()
    fn()
  }

  // Editor-gated panels (their GETs are editor+ server-side) drop for viewers.
  const canSeeEditorPanels = canEdit !== false
  const panelRows: SheetRow[] = [
    {
      key: 'storage',
      icon: 'database',
      label: t('workspace.storage', undefined, { defaultValue: 'Storage' }),
      onClick: run(() => onOpenPanel('storage')),
      disabled: !sandboxRunning,
    },
    ...(canSeeEditorPanels
      ? [
          {
            // NOT sandbox-gated: secrets and env vars are control-plane data (they
            // live in the platform database, not the container), so the panel reads
            // and writes fine while the dev sandbox hibernates.
            key: 'environment',
            icon: 'server',
            label: t('workspace.environment', undefined, { defaultValue: 'Environment' }),
            onClick: run(() => onOpenPanel('environment')),
            badge: envMissingCount,
          } as SheetRow,
        ]
      : []),
    // API keys row hidden 2026-08-19 (owner request, "for now") — matches the
    // hidden toolbar button + panel in Workspace.tsx. Re-enable by restoring
    // this entry alongside those two.
    ...(canSeeEditorPanels
      ? [
          {
            // NOT sandbox-gated: captured activity lives in the platform database and
            // the panel reads nothing else, so a sleeping sandbox is no reason to
            // block "what did my app send yesterday?".
            key: 'activity',
            icon: 'history',
            label: t('workspace.activity', undefined, { defaultValue: 'Activity' }),
            onClick: run(() => onOpenPanel('activity')),
          } as SheetRow,
        ]
      : []),
    ...(canSeeEditorPanels
      ? [
          {
            // NOT sandbox-gated: the Logs panel's Prod tab reads the deployed
            // instance, which keeps running while the dev sandbox hibernates.
            key: 'logs',
            icon: 'log',
            label: t('workspace.logs', undefined, { defaultValue: 'Logs' }),
            onClick: run(() => onOpenPanel('logs')),
          } as SheetRow,
        ]
      : []),
    {
      // Autoruns: NOT sandbox-gated — what is running and the scheduled tasks
      // are platform state; the panel itself says when the hook files need the
      // sandbox.
      key: 'autoruns',
      icon: 'zap',
      label: t('workspace.autoruns', undefined, { defaultValue: 'Autoruns' }),
      onClick: run(() => onOpenPanel('autoruns')),
    },
    {
      // NOT sandbox-gated (D6): deployment history, the live URL, build logs and
      // custom domains are control-plane / production state. Only Deploy, git
      // push and apply-workflow need the container, and DeployPanel gates those
      // three on their own.
      key: 'pipeline',
      icon: 'rocket',
      label: t('workspace.pipeline', undefined, { defaultValue: 'Pipeline' }),
      onClick: run(() => onOpenPanel('pipeline')),
    },
    ...(onToggleTerminal
      ? [
          {
            key: 'terminal',
            icon: 'terminal' as IconName,
            label: t('workspace.terminal', undefined, { defaultValue: 'Terminal' }),
            onClick: run(onToggleTerminal),
            disabled: !sandboxRunning,
          },
        ]
      : []),
  ]

  const actionRows: SheetRow[] = [
    {
      // Team is reachable on EVERY breakpoint — its panel was mounted with no
      // control anywhere that could open it. Not sandbox-gated: membership and
      // public links are platform state, not container state.
      key: 'team',
      icon: 'people',
      label: t('workspace.team', undefined, { defaultValue: 'Team' }),
      onClick: run(() => onOpenPanel('team')),
    },
    {
      key: 'report',
      icon: 'bug',
      label: t('workspace.report', undefined, { defaultValue: 'Report a bug' }),
      onClick: run(onReport),
    },
    // Share hidden when the host passes null (read-only viewer — minting is admin+).
    ...(onShare
      ? [
          {
            key: 'share',
            icon: 'share' as const,
            label: t('workspace.share', undefined, { defaultValue: 'Share project' }),
            onClick: run(onShare),
          },
        ]
      : []),
    {
      key: 'profile',
      icon: 'user',
      // Guests land in the signup modal — label the destination honestly.
      label: isGuest
        ? t('workspace.signUp', undefined, { defaultValue: 'Sign up' })
        : t('workspace.profile', undefined, { defaultValue: 'Your profile' }),
      onClick: run(onProfile),
    },
  ]

  return (
    <MobileSheet
      open={open}
      onClose={onClose}
      title={t('workspace.moreSheet.title', undefined, { defaultValue: 'Workspace' })}
      molId="workspace-more-sheet"
    >
      {panelRows.map((row) => (
        <SheetRowButton key={row.key} row={row} />
      ))}
      <div className="mol-sheet-divider" aria-hidden="true" />
      {actionRows.map((row) => (
        <SheetRowButton key={row.key} row={row} />
      ))}
      <button
        type="button"
        className="mol-sheet-row"
        data-mol-id="more-sheet-theme"
        onClick={toggleTheme}
      >
        <span className="mol-sheet-row-icon">
          <Icon name={mode === 'dark' ? 'moon' : 'sun'} size={20} />
        </span>
        <span className="mol-sheet-row-label">
          {t('theme.toggle', undefined, { defaultValue: 'Toggle theme' })}
        </span>
        <span className="mol-sheet-row-meta">
          {mode === 'dark'
            ? t('theme.dark', undefined, { defaultValue: 'Dark' })
            : t('theme.light', undefined, { defaultValue: 'Light' })}
        </span>
      </button>
    </MobileSheet>
  )
}

/**
 * One sheet row.
 * @param props - Component props.
 * @param props.row - The row spec.
 * @returns The row button.
 */
function SheetRowButton({ row }: { row: SheetRow }) {
  return (
    <button
      type="button"
      className="mol-sheet-row"
      data-mol-id={`more-sheet-${row.key}`}
      onClick={row.onClick}
      disabled={row.disabled}
    >
      <span className="mol-sheet-row-icon">
        <Icon name={row.icon} size={20} />
      </span>
      <span className="mol-sheet-row-label">{row.label}</span>
      {row.badge ? <span className="mol-sheet-row-badge">{row.badge}</span> : null}
    </button>
  )
}
